import type { writeBody } from '@porotutu/api/features/crm/organizations/model'
import type {
  historyEntryResource,
  organizationResource,
} from '@porotutu/api/features/crm/organizations/resource'
import { type HistoryWindow, lockedBodyOf, staleAfterWrite, unwrap } from '@porotutu/react/api'
import type { ApiError } from '@porotutu/react/errors'
import type { ListMeta } from '@porotutu/react/paging'
import { mutationOptions, type QueryClient, queryOptions } from '@tanstack/react-query'
import { crm } from '@/api/client'
import { ORGANIZATIONS } from '@/api/keys'

export type Organization = ReturnType<typeof organizationResource>
export type HistoryEntry = ReturnType<typeof historyEntryResource>

export type HistoryPage = { data: HistoryEntry[]; meta: ListMeta }

export type WriteOrganization = typeof writeBody.static

const RECORD_STALE = 30_000
const HISTORY_STALE = 5 * 60_000

export const organizationQuery = queryOptions({
  queryKey: [...ORGANIZATIONS, 'record'] as const,
  queryFn: async () => unwrap(await crm.organization.get()),
  staleTime: RECORD_STALE,
})

export const organizationHistoryQuery = (window: HistoryWindow) =>
  queryOptions({
    queryKey: [...ORGANIZATIONS, 'history', window] as const,
    queryFn: async (): Promise<HistoryPage> =>
      unwrap(await crm.organization.history.get({ query: window })),
    staleTime: HISTORY_STALE,
  })

export const seedOrganization = (queryClient: QueryClient, row: Organization) => {
  queryClient.setQueryData(organizationQuery.queryKey, row)
}

export const updateOrganizationMutation = (queryClient: QueryClient, record: Organization) =>
  mutationOptions<Organization, ApiError, WriteOrganization>({
    mutationKey: [...ORGANIZATIONS, 'update'],
    mutationFn: async (body) => unwrap(await crm.organization.put(lockedBodyOf(body, record))),
    onSuccess: (row) => seedOrganization(queryClient, row),
    onSettled: staleAfterWrite(queryClient, ORGANIZATIONS),
  })
